import "typeface-heebo"

import './index.css'

import { ErrorBoundary } from 'react-error-boundary'
import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
import { RecoilRoot } from 'recoil'
import React, { Suspense, useEffect, useRef } from 'react'

import {
  considerRememberingSection,
  loadFromLocalStorage,
  saveToLocalStorage,
  userObject
} from './util'
import { getFirebaseAuth } from './firebase'
import { User as FirebaseUser } from "firebase/auth"

import { haveUnreadNewsItem } from './newsItems'
import {
  useLoginModalValidationInProgress,
  useRecentState,
  useSectionState,
  useSetNewsNotification,
  useSetShowLogin,
  useShowIntroScreen,
  useUserState,
} from './data'
import Analytics from './Analytics'
import ErrorFallback from './ErrorFallback'
import Intro from './Intro'
import Login from './Login'
import Main from './Main'
import MyPurchases from './MyPurchases'
import SectionUpdater from './SectionUpdater'

//
// Not using React 18/19 because of incompatibility with Recoil and
// PopMotion.
//
// Note: There's an animation bug between Strict Mode and react-pose;
// see index.tsx.
//
// Pinned react-router-dom to 5.x because Ionic doesn't support v6:
//
// https://github.com/ionic-team/ionic-framework/issues/24177
//

const App = () => {

    const [ , setUser ] = useUserState()
    const [ section, setSection ] = useSectionState()
	const [ recent, setRecent ] = useRecentState()
    const setShowLogin = useSetShowLogin()
	const loginModalValidationInProgress = useLoginModalValidationInProgress()
	const loginModalValidationInProgressRef = useRef(loginModalValidationInProgress)
	const showIntroScreen = useShowIntroScreen()
	const setNewsNotification = useSetNewsNotification()

	useEffect(() => {
		loginModalValidationInProgressRef.current = loginModalValidationInProgress
	}, [ loginModalValidationInProgress ])

	//
	// Track user state changes.
	//
	useEffect(() => {
		let cancelled = false
		let didReceiveAuthState = false
		const auth = getFirebaseAuth()

		const handleAuthStateChanged = (result: FirebaseUser | null) => {
			if (cancelled) {
				return
			}
			if (!result) {
				console.log('Logging out.')
				setUser(null)
			} else if (result.isAnonymous) {
				console.log('Using anonymous recorder auth.')
				setUser(null)
			} else {
				setUser(userObject(result))
				// Provider/SMS modal sign-ins close themselves after Login.tsx decides
				// whether the authenticated user is allowed for the selected flow.
				if (!loginModalValidationInProgressRef.current) {
					setShowLogin(false)
				}
			}
		}

		const unsubscribe = auth.onAuthStateChanged((result: FirebaseUser | null) => {
			console.log("onAuthStateChanged()", result)
			didReceiveAuthState = true
			handleAuthStateChanged(result)
		})

		// Firestore uses the JS SDK auth state, so Recoil must not be seeded
		// from Capacitor native auth; those accounts can temporarily diverge.
		auth.authStateReady()
			.then(() => {
				if (!didReceiveAuthState) {
					console.log("authStateReady()", auth.currentUser)
					handleAuthStateChanged(auth.currentUser)
				}
			})
			.catch(err => {
				console.log("authStateReady() failed", err)
				if (!didReceiveAuthState) {
					handleAuthStateChanged(auth.currentUser)
				}
			}
		)

		// Cleanup
		return () => {
			cancelled = true
			unsubscribe()
			FirebaseAuthentication.removeAllListeners()
		}
	}, [ setUser, setShowLogin ])

    //
    // LocalStorage will hold only the ID and name of the recently selected
    // team/season. On App load, we use the ID to look up actual data
    // for each of those, which we hold in memory -- this has additional
    // data like permissions, which we don't want to save in localStorage.
    //
    useEffect(() => {

        //
        // We only want to save 'team' and 'season' in localStorage,
        // not 'game'
        //
        if (section && section.team) {
			saveToLocalStorage('section', {
                team: section.team,
                season: section.season,
            })
        }

		//
		// Keep our locally-stored list of recently viewed teams
		// up to date.
		//
		const { changed, newRecent } = considerRememberingSection(section, recent)
		if (changed) {
			setRecent(newRecent)
			// console.log('Saved new recent', newRecent)
		}
	}, [ section, recent, setRecent ])

    useEffect(() => {
        const savedSection = loadFromLocalStorage('section')
		const params = new URLSearchParams(window.location.search)
		if (params.has('team') || params.has('season')) {
			return
		}
        if (savedSection && typeof savedSection === 'object') {
            console.log("Set section in App.js", savedSection)
			// Not sure how this happens but anyway... don't allow string "undefined" as a value
			const savedSectionTyped = savedSection as Section
			const secs = [ 'team', 'season', 'game' ] as const
			secs.forEach(sec => {
				if (savedSectionTyped[sec] === 'undefined') {
					delete savedSectionTyped[sec]
				}
			})
            setSection(savedSection)
        }

    }, [ setSection ])

	//
	// See if we need to highlight a recent News item.
	//
	useEffect(() => {
		// async because we don't want to delay startup.
		const considerDisplayingNewsNotification = async () => {
			setNewsNotification(haveUnreadNewsItem())
		}
		considerDisplayingNewsNotification()
	}, [ setNewsNotification ])

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
			<Suspense fallback={<div style={{ marginTop: '4em' }}>Loading...</div>}>
				{
					showIntroScreen ? <Intro /> : <Main />
				}
				<Login />
				<Analytics />
				<MyPurchases />
				<SectionUpdater />
			</Suspense>
		</ErrorBoundary>
	)
}

const RecoilApp = () => (
    <RecoilRoot>
        <App />
    </RecoilRoot>
)

export default RecoilApp
